import { validAttemptId, type FlowEvent } from "./formFlow";

type PixelQueue = ((...args: unknown[]) => void) & { q?: unknown[][] };
type PixelWindow = Window & { oaiq?: PixelQueue };
const SDK = "https://bzrcdn.openai.com/sdk/oaiq.min.js";
const sent = new Set<string>();
let initialized = false;
let lastPage = "";

export function chatGPTMeasurementAllowed(): boolean {
    try {
        return typeof window !== "undefined" && /^(www\.)?thiscar\.com$/.test(window.location.hostname) &&
            window.localStorage.getItem("cookie-consent") === "true" &&
            !!process.env["NEXT_PUBLIC_CHATGPT_ADS_PIXEL_ID"]?.trim();
    } catch { return false; }
}

/** This public Pixel ID is not an advertiser or Conversions API credential. */
export function initializeChatGPTAds(): boolean {
    if (typeof window === "undefined") return false;
    const w = window as PixelWindow;
    if (!chatGPTMeasurementAllowed()) {
        try { w.oaiq?.("consent", false); } catch { /* Optional measurement. */ }
        return false;
    }
    try {
        if (!w.oaiq) {
            const queue: PixelQueue = (...args) => { queue.q!.push(args); };
            queue.q = [];
            w.oaiq = queue;
        }
        w.oaiq("consent", true);
        if (!initialized) {
            w.oaiq("init", { pixelId: process.env["NEXT_PUBLIC_CHATGPT_ADS_PIXEL_ID"]!.trim() });
            initialized = true;
        }
        if (!document.querySelector(`script[src="${SDK}"]`)) {
            const script = document.createElement("script");
            script.src = SDK;
            script.async = true;
            script.id = "thiscar-chatgpt-ads-pixel";
            document.head.appendChild(script);
        }
        return true;
    } catch { return false; }
}

/** Called only for server-confirmed accepted leads; never on a thank-you page visit. */
export function trackChatGPTLead(attemptId: string | undefined): void {
    if (!validAttemptId(attemptId) || !chatGPTMeasurementAllowed()) return;
    const eventId = `lead_${attemptId}`;
    const storageKey = `tc_chatgpt_${eventId}`;
    try {
        if (sent.has(eventId) || window.sessionStorage.getItem(storageKey)) return;
    } catch { /* In-memory deduplication still applies when storage is blocked. */ }
    if (!initializeChatGPTAds()) return;
    try {
        (window as PixelWindow).oaiq!("measure", "lead_created", { type: "customer_action" }, { event_id: eventId, opt_out: true });
        sent.add(eventId);
        try { window.sessionStorage.setItem(storageKey, "1"); } catch { /* Best effort. */ }
    } catch { /* Pixel failure must never affect a saved lead. */ }
}

export function trackChatGPTPage(pathname: string): void {
    if (!initializeChatGPTAds() || pathname === lastPage) return;
    try {
        // No contact fields, VINs, appraisal IDs, URL queries or form values.
        (window as PixelWindow).oaiq!("measure", "page_viewed", { type: "contents", contents: [{ id: "website", name: "THIScar", content_type: "page" }] }, { opt_out: true });
        lastPage = pathname;
    } catch { /* Optional measurement. */ }
}

export function trackChatGPTFlowEvent(event: FlowEvent): void {
    if (event["event"] === "tc_flow_success" && event["flow_outcome"] === "accepted") trackChatGPTLead(String(event["flow_instance_id"]));
}
